import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

// API related
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { endpointService } from "#src/services/endpoint";
import {
  IPassport,
  ICreatePassportRequest,
  IUpdatePassportRequest,
} from "#src/services/endpoint/passports/types";
import {
  PassportActionType,
  PassportRuleType,
  PassportPeriodType,
} from "#src/services/endpoint/passports/enums";

// Utility functions
import {
  passportPortsToTags,
  tagsToPassportPorts,
  passportDestinationsToTags,
  tagsToPassportDestinations,
  passportSourcesToTags,
  tagsToPassportSources,
} from "../../utils/passportTagConverters";

// Components
import { createToast } from "#src/common/system/toasts";
import { Tag, TagsInputWithSearchRef } from "#src/commonV2/TagsInputWithSearch";
import { timestampToSeconds } from "../components/TimeInDayPicker";

interface UsePassportFormProps {
  isOpen: boolean;
  workspaceId: string;
  selectedPassport?: IPassport | null;
  onEditSuccess: (newData: IPassport) => void;
  onAddSuccess: (newData: IPassport) => void;
  closeModal: () => void;
}

export const usePassportForm = ({
  isOpen,
  workspaceId,
  selectedPassport,
  onEditSuccess,
  onAddSuccess,
  closeModal,
}: UsePassportFormProps) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });

  // --------------- REFS ---------------

  const sourcesInputRef = useRef<TagsInputWithSearchRef>(null);
  const destinationsInputRef = useRef<TagsInputWithSearchRef>(null);
  const portsInputRef = useRef<TagsInputWithSearchRef>(null);

  // --------------- STATE ---------------

  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState<PassportRuleType>(
    selectedPassport?.type || PassportRuleType.HOST_HOST
  );
  const [action, setAction] = useState<PassportActionType>(
    selectedPassport?.action || PassportActionType.ACCEPT
  );
  const [ports, setPorts] = useState<Tag[]>([]);
  const [destinations, setDestinations] = useState<Tag[]>([]);
  const [sources, setSources] = useState<Tag[]>([]);

  // Time frame state
  const [timeFrameEnabled, setTimeFrameEnabled] = useState<boolean>(false);
  const [timeFrameType, setTimeFrameType] = useState<PassportPeriodType>(
    selectedPassport?.duration_type || PassportPeriodType.DAILY
  );
  const [timeFrameRepeatEvery, setTimeFrameRepeatEvery] = useState<number>(1);
  const [timeFrameStartTime, setTimeFrameStartTime] = useState<number>(0);
  const [timeFrameEndTime, setTimeFrameEndTime] = useState<number>(86400);
  const [timeFrameRepeatOn, setTimeFrameRepeatOn] = useState<number[]>([]);
  const [timeFrameRepeatOnDate, setTimeFrameRepeatOnDate] = useState<number>(0);

  // --------------- COMPUTED ---------------

  const isEditing = !!selectedPassport;

  const timeFrameValid = useMemo(() => {
    // Check time frame validity
    if (timeFrameEnabled) {
      if (
        !timeFrameType ||
        timeFrameRepeatEvery <= 0 ||
        timeFrameStartTime < 0 ||
        timeFrameEndTime <= timeFrameStartTime
      ) {
        return false;
      }

      // For weekly, ensure at least one day is selected
      if (
        timeFrameType === PassportPeriodType.WEEKLY &&
        (timeFrameRepeatOn.length === 0 ||
          timeFrameRepeatOn.some((day) => day < 0 || day > 6))
      ) {
        return false;
      }

      // For monthly, ensure valid day of month
      if (
        timeFrameType === PassportPeriodType.MONTHLY &&
        !timeFrameRepeatOnDate
      ) {
        return false;
      }
    }

    return true;
  }, [
    timeFrameEnabled,
    timeFrameType,
    timeFrameRepeatEvery,
    timeFrameStartTime,
    timeFrameEndTime,
    timeFrameRepeatOn,
    timeFrameRepeatOnDate,
  ]);

  const isDataValid =
    !!sources.length &&
    !!destinations.length &&
    !!ports.length &&
    type &&
    action &&
    timeFrameValid;

  // --------------- METHODS ---------------

  const clearAllSearchInputs = useCallback(() => {
    sourcesInputRef.current?.clearSearchValue();
    destinationsInputRef.current?.clearSearchValue();
    portsInputRef.current?.clearSearchValue();
  }, []);

  const handleSaveOrAdd = useCallback(async () => {
    if (!isDataValid || isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      // Prepare basic api data
      const apiPayload = {
        type,
        action,
        sources: tagsToPassportSources(sources),
        destinations: tagsToPassportDestinations(destinations),
        ports: tagsToPassportPorts(ports),
        limit_time: timeFrameEnabled,
        duration_type: timeFrameType,
        limit_start_time: secondsToTimestamp(timeFrameStartTime),
        limit_end_time: secondsToTimestamp(timeFrameEndTime),
        days: timeFrameRepeatOn,
        repeat_number: timeFrameRepeatEvery,
        limit_start_date: dateToTimestamp(timeFrameRepeatOnDate),
      };

      // Handle edit vs add scenario
      if (isEditing && selectedPassport) {
        // For update, only send what's needed according to the API type
        const updateData: IUpdatePassportRequest = {
          ...apiPayload,
          enabled: selectedPassport.enabled,
        };

        await endpointService.updatePassport(
          workspaceId,
          selectedPassport.id,
          updateData
        );

        onEditSuccess({ ...selectedPassport, ...updateData });
        createToast({
          type: "success",
          message: t("modal.addAndEdit.notification.updateSuccess"),
        });
      } else {
        // For create, use create passport request type
        const createData: ICreatePassportRequest = {
          ...apiPayload,
        };

        const { id: newPassportId } = await endpointService.createPassport(
          workspaceId,
          createData
        );

        // Get the newly created passport
        const newPassport = await endpointService.retrievePassport(
          workspaceId,
          newPassportId
        );

        onAddSuccess(newPassport);
        createToast({
          type: "success",
          message: t("modal.addAndEdit.notification.createSuccess"),
        });
      }

      clearAllSearchInputs();
      closeModal();
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    isDataValid,
    isLoading,
    type,
    action,
    sources,
    destinations,
    ports,
    timeFrameEnabled,
    timeFrameType,
    timeFrameStartTime,
    timeFrameEndTime,
    timeFrameRepeatOn,
    timeFrameRepeatEvery,
    timeFrameRepeatOnDate,
    isEditing,
    selectedPassport,
    clearAllSearchInputs,
    closeModal,
    workspaceId,
    onEditSuccess,
    t,
    onAddSuccess,
  ]);

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setType(selectedPassport?.type || PassportRuleType.HOST_HOST);
      setAction(selectedPassport?.action || PassportActionType.ACCEPT);

      // Convert passport data to tags format
      setPorts(
        selectedPassport?.ports
          ? passportPortsToTags(selectedPassport.ports)
          : []
      );
      setDestinations(
        selectedPassport?.destinations
          ? passportDestinationsToTags(selectedPassport.destinations)
          : []
      );
      setSources(
        selectedPassport?.sources
          ? passportSourcesToTags(selectedPassport.sources)
          : []
      );

      // Reset time frame state
      setTimeFrameEnabled(!!selectedPassport?.limit_time);
      setTimeFrameType(
        selectedPassport?.duration_type || PassportPeriodType.DAILY
      );
      setTimeFrameRepeatEvery(selectedPassport?.repeat_number || 1);
      setTimeFrameStartTime(
        selectedPassport?.limit_start_time
          ? timestampToSeconds(selectedPassport.limit_start_time * 1000)
          : 0
      );
      setTimeFrameEndTime(
        selectedPassport?.limit_end_time
          ? timestampToSeconds(selectedPassport.limit_end_time * 1000)
          : 86400
      ); // 24 hours in seconds
      setTimeFrameRepeatOn(selectedPassport?.days || []);
      setTimeFrameRepeatOnDate(
        selectedPassport?.limit_start_date
          ? getDateInMonth(selectedPassport.limit_start_date * 1000)
          : 0
      );
    }
  }, [isOpen, selectedPassport]);

  return {
    // State
    isLoading,
    type,
    setType,
    action,
    setAction,
    ports,
    setPorts,
    destinations,
    setDestinations,
    sources,
    setSources,

    // Time frame state
    timeFrameEnabled,
    setTimeFrameEnabled,
    timeFrameType,
    setTimeFrameType,
    timeFrameRepeatEvery,
    setTimeFrameRepeatEvery,
    timeFrameStartTime,
    setTimeFrameStartTime,
    timeFrameEndTime,
    setTimeFrameEndTime,
    timeFrameRepeatOn,
    setTimeFrameRepeatOn,
    timeFrameRepeatOnDate,
    setTimeFrameRepeatOnDate,

    // Computed
    isEditing,
    isDataValid,

    // Methods
    handleSaveOrAdd,
    clearAllSearchInputs,

    // Refs
    sourcesInputRef,
    destinationsInputRef,
    portsInputRef,

    // Translation
    t,
  };
};

// ------------------ UTILS -------------------

function getDateInMonth(timestamp: number): number {
  const date = new Date(timestamp);
  return date.getDate();
}

function secondsToTimestamp(seconds: number): number {
  const today = new Date();
  const date = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    0,
    0,
    seconds
  );
  return Math.floor(date.getTime() / 1000);
}

function dateToTimestamp(date: number): number {
  const today = new Date();
  const tempDate = new Date(today.getFullYear(), 7, date, 0, 0, 0);
  return Math.floor(tempDate.getTime() / 1000);
}
